import BigNumber from 'bignumber.js';
import html2canvas from 'html2canvas';
import moment from 'moment';
import React, { useContext, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { BsCurrencyDollar } from 'react-icons/bs';
import QRCode from 'react-qr-code';
import { MView } from '../../../styles/mobilePageStyles';
import { Column, Row, Text } from '../../../styles/reactnative';
import { ThemeContext } from '../../../theme/ThemeProvider';
import { COLORS, FONTS } from '../../constants';
import { useFetcher, useFetcherPublic } from '../../hooks/fetcher';
import { useExchangeStore } from '../../hooks/trade/exchangeStore';
import { useIndexPrice } from '../../hooks/trade/indexprice';
import DarkBack from './dark-back.png';
import LightBack from './light-back.png';
import {
  CancelBtn,
  CustomModal,
  DownloadBtn,
  ModalDesContainer,
  ModalDesName,
  ModalDesValue,
  QRContainer,
} from './styles';
import { useSystemSetting } from '@/app/hooks/system/GetSystemSetting';
import { VITE_APP_API_MEDIA_URL } from '@/app/constants/APIKEYS';

function useImageDownload(backgroundColor: string, isDarkMode: boolean) {
  const captureArea = useRef<HTMLDivElement>(null);

  async function getLocalImageUrl(url: string) {
    const response = await fetch(url, { mode: 'cors' });
    const blob = await response.blob();
    return URL.createObjectURL(blob); // blob://xxxx 형태
  }
  /* 다운로드 버튼 함수 */
  const onImageDownload = async (systemImage: string) => {
    if (!captureArea.current) return;

    if (systemImage) {
      const localUrl = await getLocalImageUrl(
        `${VITE_APP_API_MEDIA_URL}/${systemImage}`
      );

      const backgroundImgElement = new Image();
      backgroundImgElement.src = localUrl;

      backgroundImgElement.onload = async () => {
        captureArea.current!.style.backgroundImage = `url(${localUrl})`;
        captureArea.current!.style.backgroundSize = 'cover';
        captureArea.current!.style.backgroundPosition = 'center';
        captureArea.current!.style.backgroundRepeat = 'no-repeat';
        captureArea.current!.style.padding = '40px';
        captureArea.current!.style.backgroundColor = 'transparent';

        //안전하게 한 프레임 대기
        await new Promise((r) => requestAnimationFrame(r));

        const canvas = await html2canvas(captureArea.current!, {
          scale: 4,
          backgroundColor: backgroundColor,
          useCORS: true,
        });
        captureArea.current!.style.backgroundImage = '';
        captureArea.current!.style.padding = '0px';
        const element = document.createElement('a');
        element.href = canvas.toDataURL('image/png');
        element.download = 'SHARE.png';
        element.click();
      };
    } else {
      const backgroundImg = isDarkMode ? DarkBack : LightBack;
      const backgroundImgElement = document.createElement('img');
      backgroundImgElement.src = backgroundImg;
      backgroundImgElement.style.position = 'absolute';
      backgroundImgElement.style.top = '265px';
      backgroundImgElement.style.left = '39px';
      backgroundImgElement.style.width = '527.29px !important';
      backgroundImgElement.style.height = '301px !important';

      captureArea.current.appendChild(backgroundImgElement);
      captureArea.current.style.padding = '40px';

      const canvas = await html2canvas(captureArea.current, {
        scale: 4,
        backgroundColor: backgroundColor,
      });
      captureArea.current.style.padding = '0px';
      captureArea.current.removeChild(backgroundImgElement);
      const element = document.createElement('a');
      element.href = canvas.toDataURL('image/png');
      element.download = 'SHARE.png';
      element.click();
    }
  };

  // 이미지 blob 형태로 가져오기, 이부분은 아직 미완성, 필요할때 완성할 예정
  const getImageBlob = async () => {
    if (captureArea.current) {
      const canvas = await html2canvas(captureArea.current);
      canvas.toBlob((blob) => {
        if (!blob) return;
        const url = URL.createObjectURL(blob);
        return url;
      });
    }
  };

  return {
    captureArea,
    onImageDownload,
    getImageBlob,
  };
}

const ShareModal = ({ shareData, setShareData, type = 1 }) => {
  const THEMECOLORS = useContext(ThemeContext).theme.COLORS;
  const { isDarkMode, theme } = useContext(ThemeContext);
  const { EPs } = useExchangeStore();
  const [lastprice, setLastPrice] = React.useState(0);

  const [showpnl, setShowpnl] = React.useState(true);
  const [showRate, setShowRate] = React.useState(true);
  const [showprice, setShowprice] = React.useState(true);
  const [showreferral, setShowreferral] = React.useState(true);
  const { t } = useTranslation();

  const { captureArea, onImageDownload, getImageBlob } = useImageDownload(
    theme.COLORS.listIconBorder,
    isDarkMode
  );

  const { SystemSetting: backgroundImage } = useSystemSetting('CHIWEB'); // 시스템에 설정되어 있는 백그라운드 이미지지
  const systemImage =
    backgroundImage?.result === 'undefined' ? null : backgroundImage?.result;

  const { getPrice } = useIndexPrice(shareData?.exchange_pairs_id);
  const { data } = useFetcher('/referral/code', false);
  const currencyList = useFetcherPublic('/system/currency');

  const equivusd = currencyList?.data?.result?.result?.find(
    (item: any) => item?.code === 'KRW'
  );

  useEffect(() => {
    if (!shareData) return;
    if (type == 1) {
      const price = getPrice(shareData?.exchange_pairs_id);
      setLastPrice(price);
      if (!price) {
        setTimeout(() => {
          setLastPrice(getPrice(shareData?.exchange_pairs_id));
        }, 1000);
      }
    } else {
      // type=2일 때는 종료된 포지션의 평균 진입가 계산
      const openOrders =
        shareData?.orders?.filter((order: any) => order.open_close === 'O') ||
        [];
      let avgEntryPrice = 0;

      if (openOrders.length > 0) {
        let totalAmount = 0;
        let weightedSum = 0;
        openOrders.forEach((order: any) => {
          const amount = Number(order.amount || 0);
          const price = Number(order.trade_price || 0);
          totalAmount += amount;
          weightedSum += amount * price;
        });
        avgEntryPrice = totalAmount > 0 ? weightedSum / totalAmount : 0;
      }

      setLastPrice(
        avgEntryPrice && !isNaN(Number(avgEntryPrice))
          ? Number(avgEntryPrice)
          : 0
      );
    }
    setShowpnl(true);
    setShowRate(true);
    setShowprice(true);
    setShowreferral(true);
  }, [shareData]);

  if (!shareData) {
    return null;
  }
  const referralCode = data?.result?.result?.code || '';

  const EP = EPs.find((ep) => ep.id === shareData?.exchange_pairs_id);

  let pnl = 0;
  let pnlRate = 0;

  if (type == 1) {
    // 현재 포지션의 PnL 계산 시 유효성 검증
    const entryPrice = shareData?.price; // 현재 포지션의 진입가
    const amount = shareData?.amount;
    const leverage = shareData?.leverage;
    const margin = shareData?.margin;

    if (
      entryPrice &&
      !isNaN(Number(entryPrice)) &&
      amount &&
      !isNaN(Number(amount)) &&
      leverage &&
      !isNaN(Number(leverage)) &&
      lastprice &&
      !isNaN(Number(lastprice))
    ) {
      if (shareData?.long_short == 'S') {
        pnl = BigNumber(BigNumber(entryPrice).minus(lastprice))
          .multipliedBy(amount)
          .multipliedBy(leverage)
          .toNumber();
      } else {
        pnl = BigNumber(BigNumber(lastprice).minus(entryPrice))
          .multipliedBy(amount)
          .multipliedBy(leverage)
          .toNumber();
      }

      if (margin && !isNaN(Number(margin)) && Number(margin) > 0) {
        pnlRate = BigNumber(pnl).dividedBy(margin).multipliedBy(100).toNumber();
      }
    }
  } else {
    // 종료된 포지션의 PnL 계산 시 유효성 검증
    const pnlPrice = shareData?.pnl_price;
    const margin = shareData?.margin;

    if (pnlPrice && !isNaN(Number(pnlPrice))) {
      pnl = Number(pnlPrice);

      if (margin && !isNaN(Number(margin)) && Number(margin) > 0) {
        pnlRate = new BigNumber(pnlPrice)
          .dividedBy(margin)
          .multipliedBy(100)
          .toNumber();
      }
    }
  }

  return (
    // <Modal
    //   style={{ width:'480px', backgroundColor: 'blue', padding:'0px' }}
    //   // title={t("realizedProfitShare")}
    //   className={'custom-modal'}
    //   open={shareData ? true : false}
    //   footer={null}
    //   onCancel={() => {
    //     setShareData(null);
    //   }}
    // >
    //   <MView
    //     style={{
    //       justifyContent: "center",
    //       width: "100%",
    //       borderRadius: 20,
    //       alignContent: "center",
    //       justifyItems: "center",
    //       alignItems: "center",
    //       backgroundColor: 'red',
    //       height: "672px",
    //       padding:'5px'

    //     }}
    //   >
    //     <MView
    //       ref={captureArea}
    //       style={{
    //         flexDirection: "column",
    //         alignItems: "flex-start",
    //         justifyContent: "flex-start",
    //         padding: "20px",
    //         // background: `url(${IMAGES.traderBg}) no-repeat center center / cover black`,
    //         minHeight: 200,
    //         width: "350px",
    //         backgroundColor:'yellow',

    //       }}
    //     >
    //       <MView
    //         style={{
    //           flexDirection: "row",
    //           width: "100%",
    //           marginTop: 10,
    //         }}
    //       >
    //         <img
    //           alt="LOGO"
    //           src={isDarkMode ? "/images/Qbit/logo-dark.png" : "/images/Qbit/logo-light.png"}
    //           style={{ height: 20 }}
    //         />
    //       </MView>

    //       <MView
    //         style={{
    //           flexDirection: "row",
    //           width: "100%",
    //           alignItems: "center",
    //           marginTop: 10,
    //         }}
    //       >
    //         <Text
    //           style={{
    //             ...FONTS.h5,
    //             fontWeight: "500",
    //             color: THEMECOLORS.textw,
    //             marginRight: 5,
    //           }}
    //         >
    //           {EP?.pairs}
    //         </Text>
    //         <Text
    //           style={{
    //             ...FONTS.h5,
    //             fontWeight: "500",
    //             color:
    //               type == 1
    //                 ? shareData?.long_short == "S"
    //                   ? COLORS.lightRed
    //                   : COLORS.lightGreen
    //                 : shareData?.long_short == "S"
    //                 ? COLORS.lightGreen
    //                 : COLORS.lightRed,
    //             marginRight: 5,
    //           }}
    //         >
    //           {type == 1
    //             ? shareData?.long_short == "S"
    //               ? t("short")
    //               : t("long")
    //             : shareData?.long_short == "S"
    //             ? t("long")
    //             : t("short")}
    //         </Text>
    //         <Text
    //           style={{
    //             color: THEMECOLORS.textw,
    //           }}
    //         >
    //           {shareData?.leverage}X
    //         </Text>
    //       </MView>

    //       <MView
    //         style={{
    //           flexDirection: "row",
    //           width: "100%",
    //           marginTop: 10,
    //         }}
    //       >
    //         <Text
    //           size={45}
    //           style={{
    //             color:
    //               pnl > 0
    //                 ? COLORS.lightGreen
    //                 : pnl < 0
    //                 ? COLORS.lightRed
    //                 : "white",
    //             lineHeight: 1,
    //             marginBottom: 20
    //           }}
    //         >
    //           {pnl > 0 ? "+" : ""}
    //           {pnlRate.toFixed(2)}%
    //         </Text>
    //       </MView>
    //       {showpnl && (
    //         <MView
    //           style={{
    //             flexDirection: "column",
    //             width: "100%",
    //             marginTop: -10,
    //           }}
    //         >
    //           <Text
    //             size={26}
    //             style={{
    //               color:
    //                 pnl > 0
    //                   ? COLORS.lightGreen
    //                   : pnl < 0
    //                   ? COLORS.lightRed
    //                   : "white",
    //             }}
    //           >
    //             {pnl > 0 ? "+" : ""}
    //             {pnl.toFixed(3)} USDT
    //           </Text>
    //         </MView>
    //       )}

    //       <MView
    //         style={{
    //           flexDirection: "row",
    //           marginTop: 40,
    //           justifyContent: "flex-start",
    //         }}
    //       >
    //         <Text
    //           style={{
    //             minWidth: 120,
    //             justifyContent: "flex-start",
    //             color: THEMECOLORS.textw,
    //           }}
    //         >
    //           {t(type == 1 ? "markPrice" : "entryPrice")}
    //         </Text>
    //         <MView style={{ flexDirection: "column" }}>
    //           <Text style={{ marginLeft: 0, color: THEMECOLORS.textw }}>
    //             ${lastprice && !isNaN(Number(lastprice)) ? new BigNumber(lastprice).toFormat(EP?.dp || 3) : "0.000"}
    //           </Text>
    //         </MView>
    //       </MView>
    //       {showprice && (
    //         <>
    //           <MView style={{ flexDirection: "row", marginTop: 10 }}>
    //             <Text
    //               style={{
    //                 minWidth: 120,
    //                 justifyContent: "flex-start",
    //                 color: THEMECOLORS.textw,
    //               }}
    //             >
    //               {t(type == 1 ? "entryPrice" : "avgExitPrice")}
    //             </Text>

    //             <MView style={{ flexDirection: "column" }}>
    //               <Text
    //                 style={{
    //                   marginLeft: 0,
    //                   color: THEMECOLORS.textw,
    //                 }}
    //               >
    //                 $
    //                 {(() => {
    //                   if (type == 1) {
    //                     // 현재 포지션: shareData?.price (진입가)
    //                     const price = shareData?.price;
    //                     return price && !isNaN(Number(price)) ? new BigNumber(price).toFormat(EP?.dp || 3) : "0.000";
    //                   } else {
    //                     // 종료된 포지션: 클로즈 주문들의 가중평균 청산가 계산
    //                     const closeOrders = shareData?.orders?.filter((order: any) => order.open_close === 'C') || [];
    //                     let avgExitPrice = 0;

    //                     if (closeOrders.length > 0) {
    //                       let totalAmount = 0;
    //                       let weightedSum = 0;
    //                       closeOrders.forEach((order: any) => {
    //                         const amount = Number(order.amount || 0);
    //                         const price = Number(order.trade_price || 0);
    //                         totalAmount += amount;
    //                         weightedSum += amount * price;
    //                       });
    //                       avgExitPrice = totalAmount > 0 ? weightedSum / totalAmount : 0;
    //                     }

    //                     return avgExitPrice && !isNaN(Number(avgExitPrice)) ? new BigNumber(avgExitPrice).toFormat(EP?.dp || 3) : "0.000";
    //                   }
    //                 })()}
    //               </Text>
    //             </MView>
    //           </MView>
    //           <MView style={{ flexDirection: "row", marginTop: 10 }}>
    //             <Text
    //               style={{
    //                 minWidth: 120,
    //                 justifyContent: "flex-start",
    //                 color: THEMECOLORS.textw,
    //               }}
    //             >
    //               {t(type == 1 ? "entryTime" : "closed")}
    //             </Text>
    //             <Text
    //               style={{
    //                 color: THEMECOLORS.textw,
    //               }}
    //             >
    //               {moment.utc(type == 1 ? shareData?.created_at : (shareData?.closed_at || shareData?.updated_at || shareData?.created_at)).format("YYYY-MM-DD HH:mm:ss")}
    //             </Text>
    //           </MView>
    //         </>
    //       )}
    //       {showreferral && (
    //         <MView>
    //           <Text
    //             style={{
    //               color: THEMECOLORS.textw,
    //               marginTop: 20,
    //             }}
    //           >
    //             {t("referralCode")}
    //           </Text>

    //           <QRCode
    //             size={256}
    //             style={{
    //               height: "auto",
    //               maxWidth: 80,
    //               width: 80,
    //             }}
    //             value={`https://quanturmbit.com/signup?ref=${referralCode}`}
    //             viewBox={`0 0 80 80`}
    //           />
    //           <Text
    //             style={{
    //               color: THEMECOLORS.textw,
    //               marginTop: 5,
    //             }}
    //           >
    //             {referralCode}
    //           </Text>
    //         </MView>
    //       )}
    //     </MView>

    //     {/* <MView style={{ flexDirection: 'row', marginTop: 10 }}>
    // 			<Text
    // 				style={{
    // 					justifyContent: 'flex-start',
    // 					color: 'black',
    // 				}}>
    // 				<IconInfoCircle size={15} style={{ marginRight: 5 }} />
    // 				{t('sharedesc')}
    // 			</Text>
    // 		</MView>
    // 		<MView style={{ flexDirection: 'row' }}>
    // 			<Checkbox
    // 				checked={showprice}
    // 				onChange={e => setShowprice(e.target.checked)}>
    // 				{t('price')}
    // 			</Checkbox>
    // 			<Checkbox
    // 				checked={showpnl}
    // 				onChange={e => setShowpnl(e.target.checked)}>
    // 				{t('pnl')}
    // 			</Checkbox>
    // 			<Checkbox
    // 				checked={showreferral}
    // 				onChange={e => setShowreferral(e.target.checked)}>
    // 				{t('referralCode')}
    // 			</Checkbox>
    // 		</MView> */}

    //     <Divider />
    //     <MView>
    //       <Button
    //         style={{ width: "100%", marginBottom: 30 }}
    //         type="primary"
    //         onClick={() => {
    //           onImageDownload();
    //         }}
    //       >
    //         {t("imagedownload")}
    //       </Button>
    //     </MView>
    //   </MView>
    // </Modal>
    <CustomModal
      style={{ position: 'relative' }}
      open={shareData ? true : false}
      footer={null}
      onCancel={() => {
        setShareData(null);
      }}
      closable={false}
      systemImage={systemImage}
    >
      <Column
        style={{
          height: '100%',
          justifyContent: 'space-between',
          position: 'relative',
          padding: '40px',
        }}
      >
        {!systemImage && (
          <div
            style={{
              height: '301px',
              width: '527.29px',
              position: 'absolute',
              marginLeft: '39px',
              marginTop: '195px',
              zIndex: 1,
            }}
          >
            <img alt="BackIMG" src={isDarkMode ? DarkBack : LightBack} />
          </div>
        )}

        <Column ref={captureArea} style={{ position: 'relative', zIndex: 2 }}>
          {' '}
          <MView>
            <img
              alt="LOGO"
              src={
                isDarkMode
                  ? '/images/Qbit/logo-dark.png'
                  : '/images/Qbit/logo-light.png'
              }
              style={{ height: '28px', width: '134px' }}
            />
          </MView>
          <MView style={{ width: '235px', height: '128px', marginTop: '34px' }}>
            <MView
              style={{
                flexDirection: 'row',
                width: '235px',
                // width: '300px',
                alignItems: 'center',
                marginTop: '0px',
                height: '25px',
                gap: '12px',
                display: 'flex',
                // backgroundColor: 'blue',
              }}
            >
              <div
                style={{
                  fontWeight: '500',
                  color: THEMECOLORS.textw,
                  fontSize: '20px',
                  fontFamily: 'pretendard',
                  lineHeight: '100%',
                }}
              >
                {EP?.pairs}
              </div>
              <span style={{ height: '18px', color: '#474747' }}>ㅣ</span>
              <span
                style={{
                  fontSize: '20px',
                  fontWeight: '500',
                  color:
                    type == 1
                      ? shareData?.long_short == 'S'
                        ? COLORS.lightRed
                        : COLORS.lightGreen
                      : shareData?.long_short == 'S'
                        ? COLORS.lightGreen
                        : COLORS.lightRed,
                  backgroundColor:
                    type == 1
                      ? shareData?.long_short == 'S'
                        ? 'rgba(246, 71, 93, 0.1)'
                        : 'rgba(13, 203, 129, 0.1)'
                      : shareData?.long_short == 'S'
                        ? 'rgba(13, 203, 129, 0.1)'
                        : 'rgba(246, 71, 93, 0.1)',
                  borderRadius: '2px',
                  padding: '2px 6px',
                }}
              >
                {type == 1
                  ? shareData?.long_short == 'S'
                    ? 'short'
                    : 'Long'
                  : shareData?.long_short == 'S'
                    ? 'Long'
                    : 'short'}
              </span>
              <span style={{ height: '18px', color: '#474747' }}>ㅣ</span>
              <Text
                style={{
                  ...FONTS.h3,
                  color: THEMECOLORS.textw,
                  fontSize: '20px',
                  fontFamily: 'pretendard',
                  lineHeight: '100%',
                  fontWeight: '500',
                }}
              >
                {shareData?.leverage}x
              </Text>
            </MView>

            <MView style={{ marginTop: '20px', gap: '0px' }}>
              {showpnl && (
                <MView
                  style={{
                    flexDirection: 'row',
                    width: '100%',

                    // backgroundColor:'white',
                    marginBottom: '0px',
                  }}
                >
                  <div
                    style={{
                      fontSize: '36px',
                      fontFamily: 'pretendard',
                      fontWeight: '500',
                      lineHeight: '100%',
                      whiteSpace: 'nowrap',
                      color:
                        pnl > 0
                          ? COLORS.lightGreen
                          : pnl < 0
                            ? COLORS.lightRed
                            : 'white',
                    }}
                  >
                    {/* {pnl > 0 ? "+" : ""} */}
                    {new BigNumber(pnl).toFormat(
                      EP?.dp || 4
                    )} USDT
                  </div>
                </MView>
              )}

              {showRate && (
                <Text
                  size={24}
                  style={{
                    color:
                      pnl > 0
                        ? COLORS.lightGreen
                        : pnl < 0
                          ? COLORS.lightRed
                          : 'white',
                    fontWeight: '600',
                    fontFamily: 'pretendard',
                    fontSize: '24px',
                  }}
                >
                  {pnl > 0 ? '+ ' : ''}
                  {pnlRate.toFixed(2)}
                  <span
                    style={{
                      fontWeight: '500',
                      fontSize: '24px',
                      lineHeight: '100%',
                      letterSpacing: '0%',
                    }}
                  >
                    %
                  </span>
                </Text>
              )}
            </MView>
          </MView>
          <ModalDesContainer>
            {showprice && (
              <Row style={{ gap: `68px` }}>
                <Column style={{ gap: '6px' }}>
                  <ModalDesName isDarkmode={isDarkMode}>
                    {t('entryPrice')}
                  </ModalDesName>
                  <ModalDesValue>
                    <BsCurrencyDollar />
                    {(() => {
                      if (type == 1) {
                        // 현재 포지션: shareData?.price (진입가)
                        const price = shareData?.price;
                        return price && !isNaN(Number(price))
                          ? new BigNumber(price).toFormat(EP?.dp || 3)
                          : '0.000';
                      } else {
                        // 종료된 포지션: 클로즈 주문들의 가중평균 청산가 계산
                        const closeOrders =
                          shareData?.orders?.filter(
                            (order: any) => order.open_close === 'C'
                          ) || [];
                        let avgExitPrice = 0;

                        if (closeOrders.length > 0) {
                          let totalAmount = 0;
                          let weightedSum = 0;
                          closeOrders.forEach((order: any) => {
                            const amount = Number(order.amount || 0);
                            const price = Number(order.trade_price || 0);
                            totalAmount += amount;
                            weightedSum += amount * price;
                          });
                          avgExitPrice =
                            totalAmount > 0 ? weightedSum / totalAmount : 0;
                        }

                        return avgExitPrice && !isNaN(Number(avgExitPrice))
                          ? new BigNumber(avgExitPrice).toFormat(EP?.dp || 3)
                          : '0.000';
                      }
                    })()}
                  </ModalDesValue>
                </Column>

                <Column style={{ gap: '6px' }}>
                  <ModalDesName isDarkmode={isDarkMode}>
                    {t('markPrice')}
                  </ModalDesName>
                  <ModalDesValue>
                    <BsCurrencyDollar />
                    {lastprice && !isNaN(Number(lastprice))
                      ? new BigNumber(lastprice).toFormat(EP?.dp || 3)
                      : '0.000'}
                  </ModalDesValue>
                </Column>
              </Row>
            )}

            <Column style={{ gap: '6px' }}>
              <ModalDesName isDarkmode={isDarkMode}>
                {t(type == 1 ? 'entryTime' : 'endDate')}
              </ModalDesName>
              <ModalDesValue>
                {moment
                  .utc(
                    type == 1
                      ? shareData?.created_at
                      : shareData?.closed_at ||
                          shareData?.updated_at ||
                          shareData?.created_at
                  )
                  .format('YYYY-MM-DD HH:mm:ss')}
              </ModalDesValue>
            </Column>

            <Column style={{ gap: '6px' }}>
              <ModalDesName isDarkmode={isDarkMode}>
                {t('referralCode')}
              </ModalDesName>
              <Row style={{ gap: '12px', alignItems: 'center' }}>
                <QRContainer>
                  <QRCode
                    size={256}
                    style={{
                      height: 'auto',
                      maxWidth: 80,
                      width: 80,
                    }}
                    value={`https://quanturmbit.com/signup?ref=${referralCode}`}
                    viewBox={`0 0 80 80`}
                  />
                </QRContainer>
                {referralCode}
              </Row>
            </Column>
          </ModalDesContainer>
        </Column>

        <Column style={{ gap: '8px', paddingTop: '20px' }}>
          <Row
            style={{ justifyContent: 'center', gap: '10px', height: '40px' }}
          >
            <DownloadBtn
              onClick={() => {
                onImageDownload(systemImage);
              }}
            >
              {t('imagedownload')}
            </DownloadBtn>
            <CancelBtn onClick={() => setShareData(null)}>
              {t('CLOSE')}
            </CancelBtn>
          </Row>
          <Row style={{ justifyContent: 'center', gap: '16px' }}>
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                color: THEMECOLORS.textw,
              }}
            >
              <input
                type="checkbox"
                checked={showpnl}
                onChange={(e) => setShowpnl(e.target.checked)}
              />
              {t('Profit')}
            </label>
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                color: THEMECOLORS.textw,
              }}
            >
              <input
                type="checkbox"
                checked={showRate}
                onChange={(e) => setShowRate(e.target.checked)}
              />
              {t('ROI')}
            </label>
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                color: THEMECOLORS.textw,
              }}
            >
              <input
                type="checkbox"
                checked={showprice}
                onChange={(e) => setShowprice(e.target.checked)}
              />
              {t('price')}
            </label>
          </Row>
        </Column>
      </Column>
      {/* <MView
        style={{
          flexDirection: 'row',
          width: '224px',
          // height:'249px',
          flexWrap: 'wrap',
          marginTop: '42px',
          backgroundColor: 'red',
        }}
      >
        <MView
          style={{
            flexDirection: 'row',
            marginTop: 40,
            justifyContent: 'flex-start',
          }}
        >
          <Text
            style={{
              minWidth: 120,
              justifyContent: 'flex-start',
              color: THEMECOLORS.textw,
            }}
          >
            {t(type == 1 ? 'markPrice' : 'entryPrice')}
          </Text>
          <MView style={{ flexDirection: 'column' }}>
            <Text style={{ marginLeft: 0, color: THEMECOLORS.textw }}>
              $
              {lastprice && !isNaN(Number(lastprice))
                ? new BigNumber(lastprice).toFormat(EP?.dp || 3)
                : '0.000'}
            </Text>
          </MView>
        </MView>
        {showprice && (
          <>
            <MView style={{ flexDirection: 'row', marginTop: 10 }}>
              <Text
                style={{
                  minWidth: 120,
                  justifyContent: 'flex-start',
                  color: THEMECOLORS.textw,
                }}
              >
                {t(type == 1 ? 'entryPrice' : 'avgExitPrice')}
              </Text>

              <MView style={{ flexDirection: 'column' }}>
                <Text
                  style={{
                    marginLeft: 0,
                    color: THEMECOLORS.textw,
                  }}
                >
                  $
                  {(() => {
                    if (type == 1) {
                      // 현재 포지션: shareData?.price (진입가)
                      const price = shareData?.price;
                      return price && !isNaN(Number(price))
                        ? new BigNumber(price).toFormat(EP?.dp || 3)
                        : '0.000';
                    } else {
                      // 종료된 포지션: 클로즈 주문들의 가중평균 청산가 계산
                      const closeOrders =
                        shareData?.orders?.filter(
                          (order: any) => order.open_close === 'C'
                        ) || [];
                      let avgExitPrice = 0;

                      if (closeOrders.length > 0) {
                        let totalAmount = 0;
                        let weightedSum = 0;
                        closeOrders.forEach((order: any) => {
                          const amount = Number(order.amount || 0);
                          const price = Number(order.trade_price || 0);
                          totalAmount += amount;
                          weightedSum += amount * price;
                        });
                        avgExitPrice =
                          totalAmount > 0 ? weightedSum / totalAmount : 0;
                      }

                      return avgExitPrice && !isNaN(Number(avgExitPrice))
                        ? new BigNumber(avgExitPrice).toFormat(EP?.dp || 3)
                        : '0.000';
                    }
                  })()}
                </Text>
              </MView>
            </MView>
            <MView style={{ flexDirection: 'row', marginTop: 10 }}>
              <Text
                style={{
                  minWidth: 120,
                  justifyContent: 'flex-start',
                  color: THEMECOLORS.textw,
                }}
              >
                {t(type == 1 ? 'entryTime' : 'closed')}
              </Text>
              <Text
                style={{
                  color: THEMECOLORS.textw,
                }}
              >
                {moment
                  .utc(
                    type == 1
                      ? shareData?.created_at
                      : shareData?.closed_at ||
                          shareData?.updated_at ||
                          shareData?.created_at
                  )
                  .format('YYYY-MM-DD HH:mm:ss')}
              </Text>
            </MView>
          </>
        )}
        {showreferral && (
          <MView>
            <Text
              style={{
                color: THEMECOLORS.textw,
                marginTop: 20,
              }}
            >
              {t('referralCode')}
            </Text>

            <QRCode
              size={256}
              style={{
                height: 'auto',
                maxWidth: 80,
                width: 80,
              }}
              value={`https://quanturmbit.com/signup?ref=${referralCode}`}
              viewBox={`0 0 80 80`}
            />
            <Text
              style={{
                color: THEMECOLORS.textw,
                marginTop: 5,
              }}
            >
              {referralCode}
            </Text>
          </MView>
        )}
      </MView> */}
    </CustomModal>
  );
};
export default React.memo(ShareModal);
